/**
 * UniversalExportModal Component
 *
 * Generic export modal that works for any entity type.
 * Driven by configuration object provided via props.
 *
 * FEATURES:
 * - Config-driven filter and column sections
 * - Conditional section visibility based on row selection
 * - React.memo optimization
 * - Integrates with existing ExportModal and ExportSection components
 * - Full type safety with NO 'as any' casts
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Tooltip } from '@/components/Tooltip';
import { ExportModal } from '@/sections/export/components/ExportModal';
import type { ExportOption as SectionExportOption } from '@/sections/export/components/ExportSection';
import { ExportSection } from '@/sections/export/components/ExportSection';
import type { DropdownOption } from '@/types/dropdown.types';

import { useUniversalExport } from './hooks/useUniversalExport';
import type {
  ExportableEntity,
  ExportFormat,
  ExportOption,
  UniversalExportModalProps,
} from './types';

/**
 * Helper: Convert ExportFormat to DropdownOption
 */
function formatToDropdownOption(format: ExportFormat): DropdownOption {
  return {
    label: format.label,
    value: format.value,
  };
}

/**
 * Helper: Convert DropdownOption to ExportFormat
 */
function dropdownOptionToFormat(option: DropdownOption): ExportFormat {
  return {
    label: option.label,
    value: String(option.value || option.label),
  };
}

/**
 * Helper: Convert ExportOption to SectionExportOption
 */
function toSectionExportOption(option: ExportOption): SectionExportOption {
  return {
    label: option.label,
    value: option.value,
    severity: option.severity,
    variant: option.variant,
    icon: option.icon,
    count: option.count,
  };
}

/**
 * Helper: Convert SectionExportOption to ExportOption
 */
function fromSectionExportOption(option: SectionExportOption): ExportOption {
  return {
    label: option.label,
    value: option.value,
    severity: option.severity,
    variant: option.variant,
    icon: option.icon,
    count: typeof option.count === 'number' ? option.count : undefined,
  };
}

/**
 * UniversalExportModal Component
 *
 * @param props - Modal props including config and selection state
 *
 * USAGE:
 * ```typescript
 * <UniversalExportModal
 *   open={showModal}
 *   onClose={handleClose}
 *   config={assetExportConfig}
 *   selectedRows={selectedAssetKeys}
 *   onClearSelection={clearSelection}
 * />
 * ```
 */
function UniversalExportModalComponent<TEntity extends ExportableEntity>({
  open,
  onClose,
  config,
  selectedRows,
  onClearSelection,
  activeFilters,
  activeQuery,
  totalMatchingCount,
}: UniversalExportModalProps<TEntity>) {
  // Use universal export hook with entity config
  const exportHook = useUniversalExport(config, activeQuery);

  // Check if active filters are provided
  const hasActiveFilters = activeFilters && activeFilters.length > 0;

  // UX divergence #5: third export scope option — only shown when filters/query active.
  // Tracks whether the user has opted into "All matching your current filters" mode.
  // Hidden when the user has already selected every matching row, since it would
  // be a duplicate of "Selected rows (N)".
  const isAllMatchingAlreadySelected =
    typeof totalMatchingCount === 'number' &&
    totalMatchingCount > 0 &&
    selectedRows.length >= totalMatchingCount;
  const hasFilterScope =
    Boolean(activeFilters?.length || activeQuery) &&
    !isAllMatchingAlreadySelected;
  // Export scope: 'selected' uses selectedRows, 'filtered' uses activeQuery,
  // 'all' exports everything (no row or filter constraint).
  // Default: 'selected' when rows exist, 'all' otherwise.
  const defaultScope =
    selectedRows.length > 0 ? 'selected' : hasFilterScope ? 'filtered' : 'all';
  const [exportScope, setExportScope] = useState<
    'selected' | 'filtered' | 'all'
  >(defaultScope);

  // FE4: Reset scope to defaultScope whenever the modal opens (or defaultScope changes
  // while open), so stale selection from a previous open does not persist.

  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setExportScope(defaultScope);
  }, [open, defaultScope]);

  // Convert selectedRows to string array for internal use
  const selectedRowStrings = useMemo(
    () => selectedRows as readonly string[],
    [selectedRows]
  );

  // Determine which filter sections to show
  const visibleFilterSections = useMemo(
    () =>
      config.filterSections.filter(section => {
        // Hide sections that shouldn't show when rows are selected
        if (selectedRowStrings.length > 0 && !section.showWhenSelected) {
          return false;
        }
        return true;
      }),
    [config.filterSections, selectedRowStrings.length]
  );

  // Handle export execution with immediate modal closure for async exports
  const handleExport = useCallback((): void => {
    // UX divergence #5: determine rows to pass based on export scope.
    // 'selected' → pass the explicit row keys
    // 'filtered' / 'all' → pass empty so the hook uses activeQuery or exports all
    const rowsForExport = exportScope === 'selected' ? selectedRowStrings : [];

    // Start export asynchronously (don't await, let it run in background)
    // The useAsyncExports hook handles:
    // - Validation errors (will show toast)
    // - Toast notifications ("Exporting...", "Download" button on success)
    // - Job polling and status updates
    // - Error handling for async failures
    exportHook
      .executeExport(rowsForExport as readonly string[], exportScope)
      .catch(error => {
        // Log validation errors for debugging
        // (async errors are already handled by useAsyncExports toast)
        console.error('[UniversalExportModal] Export error:', error);
      });

    // Close modal IMMEDIATELY after starting export (don't wait for completion)
    // This provides the async UX: modal closes → toast shows "Exporting..." → polling happens in background
    onClose();
    if (exportScope === 'selected') {
      onClearSelection?.();
    }
  }, [exportHook, selectedRowStrings, onClose, onClearSelection, exportScope]);

  // Convert config formats to DropdownOptions for ExportModal (memoized)
  const formatOptions = useMemo(
    () => config.formats.map(formatToDropdownOption),
    [config.formats]
  );

  // Convert format for ExportModal (memoized)
  const exportFormatDropdown = useMemo(
    () => formatToDropdownOption(exportHook.state.format),
    [exportHook.state.format]
  );

  // Handle format change (wrap with callback)
  const handleFormatChange = useCallback(
    (option: DropdownOption): void => {
      exportHook.updateFormat(dropdownOptionToFormat(option));
    },
    [exportHook]
  );

  // Map column configs to SectionExportOptions (memoized)
  const columnOptions = useMemo(
    (): SectionExportOption[] =>
      config.columns.map(col => ({
        label: col.label,
        value: col.id,
      })),
    [config.columns]
  );

  // Create memoized filter update handler to prevent inline function creation
  const createFilterUpdateHandler = useCallback(
    (sectionId: string) =>
      (options: SectionExportOption[]): void => {
        exportHook.updateFilter(
          sectionId,
          options.map(fromSectionExportOption)
        );
      },
    [exportHook]
  );

  // Create memoized column update handler
  const handleColumnsUpdate = useCallback(
    (options: SectionExportOption[]): void => {
      exportHook.updateColumns(options.map(fromSectionExportOption));
    },
    [exportHook]
  );

  return (
    <ExportModal
      open={open}
      onClose={onClose}
      type={config.entityType}
      selected={
        selectedRowStrings.length > 0 && onClearSelection
          ? {
              selectedRows: Array.from(selectedRowStrings),
              onClearSelectedRows: onClearSelection,
            }
          : undefined
      }
      disabled={exportHook.isExportDisabled(
        exportScope === 'selected'
          ? selectedRowStrings
          : ([] as readonly string[])
      )}
      loading={exportHook.isLoading}
      exportFormat={exportFormatDropdown}
      onChangeExportFormat={handleFormatChange}
      onExport={handleExport}
      formatOptions={formatOptions}
    >
      {/* UX divergence #5: export scope options — order: Selected → Filtered → All */}
      {(selectedRowStrings.length > 0 || hasFilterScope) && (
        <fieldset className="border-chariot-stroke mb-4 flex flex-col gap-2 border-b pb-4">
          <legend className="sr-only">Export scope</legend>
          {/* 1. Selected rows (N) — only shown when rows are explicitly selected */}
          {selectedRowStrings.length > 0 && (
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                name="export-scope"
                value="selected"
                checked={exportScope === 'selected'}
                onChange={() => setExportScope('selected')}
                className="accent-chariot-brand-primary"
                aria-label={`Selected rows (${selectedRowStrings.length})`}
              />
              {`Selected rows (${selectedRowStrings.length})`}
            </label>
          )}
          {/* 2. All matching your current filters — only shown when filters/query active */}
          {hasFilterScope && (
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                name="export-scope"
                value="filtered"
                checked={exportScope === 'filtered'}
                onChange={() => setExportScope('filtered')}
                className="accent-chariot-brand-primary"
                aria-label="All matching your current filters"
              />
              {'All matching your current filters'}
            </label>
          )}
          {/* 3. All data — always shown as the final fallback option */}
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="radio"
              name="export-scope"
              value="all"
              checked={exportScope === 'all'}
              onChange={() => setExportScope('all')}
              className="accent-chariot-brand-primary"
              aria-label="All data"
            />
            {'All data'}
          </label>
        </fieldset>
      )}

      {/* Conditionally render filter pills or interactive filter sections */}
      {hasActiveFilters ? (
        <div className="mb-4 space-y-3">
          <div className="text-default text-sm font-medium">Active Filters</div>
          <div className="flex flex-wrap gap-2">
            {activeFilters.map(filter => (
              <Tooltip
                key={filter.label}
                placement="bottom"
                title={
                  <ul className="min-w-[140px] space-y-1">
                    {filter.values.map(v => (
                      <li key={v} className="text-sm">
                        {v}
                      </li>
                    ))}
                  </ul>
                }
              >
                <span className="border-chariot-brand-primary bg-chariot-background-secondary text-chariot-text-primary inline-flex items-center rounded-full border border-dashed px-2.5 py-1 text-xs font-bold">
                  {filter.label}
                  <span className="text-chariot-brand-primary mx-1.5">|</span>
                  <span className="bg-chariot-brand-primary inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] leading-none font-bold text-white">
                    {filter.values.length}
                  </span>
                </span>
              </Tooltip>
            ))}
          </div>
        </div>
      ) : (
        // Render interactive filter sections
        visibleFilterSections.map(section => (
          <ExportSection
            key={section.id}
            label={section.label}
            options={section.options.map(toSectionExportOption)}
            styleType={section.styleType || 'table'}
            selected={
              exportHook.state.filters[section.id]?.map(
                toSectionExportOption
              ) || []
            }
            setSelected={createFilterUpdateHandler(section.id)}
          />
        ))
      )}

      {/* Always render columns section */}
      <ExportSection
        label="Columns"
        styleType="columns"
        options={columnOptions}
        selected={exportHook.state.columns.map(toSectionExportOption)}
        setSelected={handleColumnsUpdate}
      />
    </ExportModal>
  );
}

/**
 * Memoized version for performance optimization
 *
 * Custom comparison function prevents unnecessary re-renders by checking:
 * - open state changes (modal visibility)
 * - config reference changes (export configuration)
 * - selectedRows array length and content (row selection state)
 * - activeFilters array reference and content
 * - activeQuery reference
 *
 * Returns true if props are equal (skip re-render), false if different (re-render)
 */
const MemoizedUniversalExportModal = React.memo(
  UniversalExportModalComponent,
  <TEntity extends ExportableEntity>(
    prevProps: UniversalExportModalProps<TEntity>,
    nextProps: UniversalExportModalProps<TEntity>
  ): boolean => {
    // Check open state - critical for modal visibility
    if (prevProps.open !== nextProps.open) {
      return false;
    }

    // Check config reference - if config changes, we need to re-render
    if (prevProps.config !== nextProps.config) {
      return false;
    }

    // Check selectedRows length - quick first pass
    if (prevProps.selectedRows.length !== nextProps.selectedRows.length) {
      return false;
    }

    // Check selectedRows content - verify each item is the same
    // Uses strict equality check for primitive values or reference equality for objects
    const rowsEqual = prevProps.selectedRows.every(
      (row, index) => row === nextProps.selectedRows[index]
    );
    if (!rowsEqual) {
      return false;
    }

    // Check onClose reference (typically stable but worth checking)
    if (prevProps.onClose !== nextProps.onClose) {
      return false;
    }

    // Check onClearSelection reference (optional prop)
    if (prevProps.onClearSelection !== nextProps.onClearSelection) {
      return false;
    }

    // Check activeFilters reference and content
    if (prevProps.activeFilters !== nextProps.activeFilters) {
      // If references differ, compare content
      if (
        !prevProps.activeFilters ||
        !nextProps.activeFilters ||
        prevProps.activeFilters.length !== nextProps.activeFilters.length
      ) {
        return false;
      }
      // Compare each filter
      const filtersEqual = prevProps.activeFilters.every((filter, index) => {
        const nextFilter = nextProps.activeFilters![index];
        return (
          filter.label === nextFilter.label &&
          filter.values.length === nextFilter.values.length &&
          filter.values.every(
            (val, valIdx) => val === nextFilter.values[valIdx]
          )
        );
      });
      if (!filtersEqual) {
        return false;
      }
    }

    // Check activeQuery reference
    if (prevProps.activeQuery !== nextProps.activeQuery) {
      return false;
    }

    // All props are equal - skip re-render for performance
    return true;
  }
);

// Set display name for React DevTools
MemoizedUniversalExportModal.displayName = 'UniversalExportModal';

// Export with proper typing - preserve generic type support
export const UniversalExportModal =
  MemoizedUniversalExportModal as typeof UniversalExportModalComponent;
